import { Link } from "react-router-dom";
import { ArrowRight, Check, Gift, Hash, ScanFace, Shirt, Split, Timer, Users } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import ContentImage from "@/components/ContentImage";
import EventTypesGrid from "@/components/EventTypesGrid";
import GuideLearnMore from "@/components/guides/GuideLearnMore";
import TestimonialLogo from "@/components/TestimonialLogo";
import { GuideImg } from "@/components/guides/GuideBody";
import { Button } from "@/components/ui/button";
import { GUIDE_IMAGES } from "@/content/guides/images";
import { guidePath } from "@/content/guides";
import { isGuidesEnabled } from "@/lib/guidesFlag";
import { useHostname } from "@/lib/ssr";
import { companyOf, titleOf } from "@/content/testimonials/logos";
import { useTestimonials } from "@/hooks/useTestimonials";
import { ORGANISER_FAQ } from "@/content/forOrganisers";
import { FACE_DATA_POLICY_PATH } from "@/content/faceDataPolicy";
import { WEB_UPLOADER_PATH } from "@/content/webUploader";

// /for-organisers: Photohawk Studio for race and event organisers (English only). Copy and FAQ in
// src/content/forOrganisers (the FAQ also feeds the page's FAQPage JSON-LD); its <head>, sitemap entry and link-preview
// card are in src/lib/seo/pages.ts. Every image is a real product screen or a diagram from src/content/media, or the
// revenue split guide's screenshot (demo data).

const FIND_WAYS = [
  { icon: Hash, title: "Number search", text: "Participants type the number they wore: a bib, a competitor number or the number on a bike. Numbers are read from the photos after upload, with no tagging by hand." },
  { icon: ScanFace, title: "Selfie search", text: "Participants take a selfie and see the photos they're in, from the whole event. The selfie is used for that one search, then discarded." },
  { icon: Shirt, title: "Browse by time, place and colour", text: "Search by the time they were there, browse a collection for each start, stage, match or ring, and filter by clothing colour when a number is hidden." },
  { icon: Timer, title: "Live while people still care", text: "Previews go up first, on every plan, so the gallery is searchable soon after the photos come off the course." },
];

const MODELS = [
  {
    icon: Gift,
    title: "Free photos, paid by a sponsor",
    text: "Make a gallery free to download and let a sponsor cover it. Their name goes on the gallery; every participant gets their photos.",
    link: "/event-photography/free-race-photos-paid-by-sponsors",
    linkLabel: "Free race photos paid by sponsors",
  },
  {
    icon: Users,
    title: "Your photographers, one gallery",
    text: "Invite the photographers you hire into your workspace. They upload into your galleries from their browser, and participants search one gallery for the whole event.",
    link: WEB_UPLOADER_PATH,
    linkLabel: "How the web uploader works",
  },
  {
    icon: Split,
    title: "Sales shared, automatically",
    text: "On the Max plan, share each gallery's sales with your photographers, a club or a charity. Photohawk works out every share on every sale and pays them for you.",
    link: guidePath("split-gallery-revenue"),
    linkLabel: "How revenue splitting works",
    guide: true,
  },
];

const ForOrganisers = () => {
  const { testimonials } = useTestimonials();
  const guides = isGuidesEnabled(useHostname());
  const split = GUIDE_IMAGES["split-list"];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-hawk-navy pb-20 pt-32 text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_30%,rgba(234,138,35,0.30),transparent_55%)]" aria-hidden="true" />
          <div className="container relative z-10 mx-auto grid items-center gap-12 px-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
            <div className="max-w-2xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-sm">
                <Users size={16} className="text-hawk-orange" aria-hidden="true" />
                <span className="text-sm font-medium">For event, race and competition organisers</span>
              </div>
              <h1 className="mb-6 font-display text-4xl font-bold md:text-5xl">
                Photos for every participant, <span className="text-gradient-primary">found by selfie, number or time</span>
              </h1>
              <p className="mb-8 text-lg text-white/80 md:text-xl">
                Photohawk Studio is the event photography platform behind your photo gallery, for races and rides, matches, shows, festivals
                and conferences: participants find their own photos in seconds, download them free or buy them, and the money is shared with
                the people who made the day happen.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button variant="hero" size="lg" className="group">
                    Start free
                    <ArrowRight className="transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/find-your-photos">
                  <Button variant="heroOutline" size="lg">
                    See a gallery search
                  </Button>
                </Link>
              </div>
            </div>
            <figure className="m-0">
              <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                <ContentImage name="bib-number-search-dialog" eager sizes="(min-width: 1024px) 560px, 92vw" className="block h-auto w-full" />
              </div>
              <figcaption className="mt-3 text-sm text-white/60">The Find your photos box in a race gallery: a number, a selfie, or both.</figcaption>
            </figure>
          </div>
        </section>

        {/* How participants find their photos */}
        <section className="py-20" aria-labelledby="find-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-14 max-w-3xl text-center">
              <h2 id="find-heading" className="mb-4 font-display text-3xl font-bold md:text-4xl">
                Every participant finds <span className="text-hawk-orange">their own photos</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                No scrolling through thousands of thumbnails. Selfie search, number search and time search do the finding, so your inbox doesn't
                fill with "where are my photos?"
              </p>
            </div>
            <ul className="mx-auto grid max-w-6xl list-none gap-6 p-0 sm:grid-cols-2 lg:grid-cols-4">
              {FIND_WAYS.map((w) => (
                <li key={w.title} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-hawk-orange/10 text-hawk-orange">
                    <w.icon size={22} aria-hidden="true" />
                  </span>
                  <h3 className="mb-2 font-display text-lg font-bold">{w.title}</h3>
                  <p className="text-sm text-muted-foreground">{w.text}</p>
                </li>
              ))}
            </ul>
            <div className="mx-auto mt-14 grid max-w-6xl items-center gap-10 md:grid-cols-2">
              <figure className="m-0">
                <div className="overflow-hidden rounded-2xl border border-border shadow-lg">
                  <ContentImage name="selfie-search-results" sizes="(min-width: 768px) 560px, 92vw" className="block h-auto w-full" />
                </div>
                <figcaption className="mt-3 text-sm text-muted-foreground">Selfie search results: only the photos this participant is in.</figcaption>
              </figure>
              <div className="space-y-4 text-muted-foreground">
                <h3 className="font-display text-2xl font-bold text-foreground">Live the same day</h3>
                <p>
                  Participants look for their photos in the hours after the event. With previews first, a 10,000-photo gallery is live in about
                  8 minutes on a 20 Mbps upload, while the originals follow in the background.
                </p>
                <p>
                  Put the gallery link in your results email and on your results page, and people go from their finish time to their photos in one
                  click. Lost the link? They can find your event in Photohawk's <Link to="/find-your-photos" className="font-medium text-hawk-orange hover:underline">Find your photos</Link> directory.
                </p>
                <p className="font-medium text-foreground">75 million photos uploaded and 185 million people detected, at events in 29 countries.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Every kind of event (src/components/EventTypesGrid.tsx) */}
        <section className="bg-secondary/30 py-20" aria-labelledby="events-heading" data-testid="organiser-events">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <h2 id="events-heading" className="mb-4 font-display text-3xl font-bold md:text-4xl">
                Races, matches, shows <span className="text-hawk-orange">and stages</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Wherever people want photos of themselves, they look for them the same way: by selfie, by the number they wore, or by when and
                where they were. Each gallery uses what suits your event.
              </p>
            </div>
            <EventTypesGrid className="mx-auto max-w-6xl" />
          </div>
        </section>

        {/* Ways to run it */}
        <section className="py-20" aria-labelledby="models-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-14 max-w-3xl text-center">
              <h2 id="models-heading" className="mb-4 font-display text-3xl font-bold md:text-4xl">
                Free, for sale, <span className="text-hawk-orange">or both</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Each gallery sells photos or gives them away as free downloads. Choose what suits the event, your sponsors and your photographers.
              </p>
            </div>
            <ul className="mx-auto grid max-w-6xl list-none gap-6 p-0 md:grid-cols-3">
              {MODELS.filter((m) => !m.guide || guides).map((m) => (
                <li key={m.title} className="flex flex-col rounded-2xl bg-card p-7 shadow-lg">
                  <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold text-white">
                    <m.icon size={24} aria-hidden="true" />
                  </span>
                  <h3 className="mb-2 font-display text-xl font-bold">{m.title}</h3>
                  <p className="mb-5 text-muted-foreground">{m.text}</p>
                  <Link to={m.link} className="mt-auto inline-flex items-center gap-1.5 font-semibold text-hawk-orange hover:underline">
                    {m.linkLabel} <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mx-auto mt-14 grid max-w-6xl items-center gap-10 lg:grid-cols-2">
              <figure className="m-0">
                <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-lg">
                  <ContentImage name="sponsor-free-download-photos-diagram" sizes="(min-width: 1024px) 560px, 92vw" className="block h-auto w-full" />
                </div>
                <figcaption className="mt-3 text-sm text-muted-foreground">How sponsor-paid free photos work, from the sponsor to every participant's download.</figcaption>
              </figure>
              {split && (
                <figure className="m-0">
                  <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-lg">
                    <GuideImg
                      img={split}
                      alt="A gallery's Revenue split: a photographer on 30% of the photos they took, a two-person crew on 20%, a running club on 10% and a finish-line sponsor on 5% of one collection, and the workspace keeps at least 35% of every sale."
                      sizes="(min-width: 1024px) 560px, 92vw"
                    />
                  </div>
                  <figcaption className="mt-3 text-sm text-muted-foreground">A gallery's revenue split: who gets paid, for which sales, and what you keep. Demo data.</figcaption>
                </figure>
              )}
            </div>
          </div>
        </section>

        {/* Privacy */}
        <section className="py-20" aria-labelledby="privacy-heading">
          <div className="container mx-auto grid max-w-6xl items-center gap-10 px-4 md:grid-cols-2">
            <div>
              <h2 id="privacy-heading" className="mb-4 font-display text-3xl font-bold md:text-4xl">
                Face search, <span className="text-hawk-orange">handled carefully</span>
              </h2>
              <ul className="space-y-3 text-muted-foreground">
                {[
                  "The selfie is used for that one search and then discarded.",
                  "Bib search and browsing work without any face data.",
                  "Photohawk's face data policy sets out what's kept, for how long, and how people ask for it to be deleted.",
                  "Our GDPR checklist lists what to tell participants before the event.",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="mt-0.5 flex-shrink-0 text-hawk-orange" size={20} aria-hidden="true" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
                <Link to={FACE_DATA_POLICY_PATH} className="font-semibold text-hawk-orange hover:underline">Face data policy</Link>
                <Link to="/event-photography/gdpr-checklist-face-search-for-organisers" className="font-semibold text-hawk-orange hover:underline">
                  GDPR checklist for organisers
                </Link>
              </div>
            </div>
            <figure className="m-0">
              <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-lg">
                <ContentImage name="face-search-gdpr-checklist-diagram" sizes="(min-width: 768px) 560px, 92vw" className="block h-auto w-full" />
              </div>
            </figure>
          </div>
        </section>

        {/* Testimonials (the same list as the home page; hidden while empty) */}
        {testimonials.length > 0 && (
          <section className="bg-secondary/30 py-20" aria-labelledby="org-reviews">
            <div className="container mx-auto px-4">
              <h2 id="org-reviews" className="mb-10 text-center font-display text-3xl font-bold md:text-4xl">
                Organisers and photographers <span className="text-hawk-orange">on Photohawk</span>
              </h2>
              <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
                {testimonials.map((q) => (
                  <figure key={q.id} className="m-0 flex flex-col gap-4 rounded-2xl bg-card p-7 shadow-lg" data-testid="organiser-testimonial">
                    <TestimonialLogo role={q.role} className="border-b border-border pb-3" />
                    <blockquote className="m-0 text-lg leading-relaxed">“{q.quote}”</blockquote>
                    <figcaption className="mt-auto text-sm text-muted-foreground">
                      <strong className="text-foreground">{q.name}</strong> · {companyOf(q.role) ? titleOf(q.role) : q.role}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ (also the page's FAQPage JSON-LD) */}
        <section className="py-20" aria-labelledby="org-faq">
          <div className="container mx-auto max-w-4xl px-4">
            <h2 id="org-faq" className="mb-12 text-center font-display text-3xl font-bold md:text-4xl">
              Questions organisers ask
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {ORGANISER_FAQ.map((f) => (
                <div key={f.q} className="rounded-xl bg-card p-6 shadow-lg" data-testid="organiser-faq">
                  <h3 className="mb-2 font-display font-bold">{f.q}</h3>
                  <p className="text-sm text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* More for organisers */}
        <section className="pb-8" aria-labelledby="org-more">
          <div className="container mx-auto max-w-4xl px-4">
            <h2 id="org-more" className="mb-4 text-sm font-bold uppercase tracking-[0.1em] text-muted-foreground">More for organisers</h2>
            <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-2">
              {[
                ["/event-photography/offer-race-photos-to-participants", "How race organisers can offer photos to every participant"],
                ["/event-photography/add-photos-to-race-results", "Adding photos to race results"],
                ["/event-photography/choosing-an-event-photography-partner", "Choosing an event photography partner"],
                ["/event-photography/race-photo-marketing-emails-and-sms", "Getting race photos seen: email, SMS and app timing"],
                ["/blog/bib-number-search-race-photos", "Bib number search for race photos"],
                ["/blog/selfie-search-for-event-photos", "Selfie search for event photos"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link to={to} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 font-medium hover:border-hawk-orange">
                    {label} <ArrowRight size={16} aria-hidden="true" className="flex-none text-hawk-orange" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <GuideLearnMore slugs={["split-gallery-revenue", "invited-photographer-uploads"]} />

        {/* CTA */}
        <section className="bg-gradient-to-r from-hawk-orange to-hawk-gold py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold text-white md:text-4xl">Give every participant their photos</h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-white/90">
              Start free on Lite, invite your photographers, and have your gallery searchable on the day.
            </p>
            <Link to="/register">
              <Button size="lg" className="group bg-hawk-navy px-8 text-lg text-white hover:bg-hawk-navy/90">
                Create your free workspace
                <ArrowRight className="transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ForOrganisers;
